import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import utilities from "../Helpers/utilities";
import { useWindowPopoverState } from "../Components/Common/Composables/useWindowPopoverState";
import { useAppState } from "../Stores/AppState";
import { useRoute, useRouter } from "vue-router";
import { ROUTE_NAMES } from "../Constants/routes";
import { useConversation } from "../Stores/Conversation";
import { useChat } from "../Components/Chat/Store/Chat";
import { useRightDrawerStore } from "./RightDrawer";

/** Routes where chat/drawer “primary panel” behavior matches work/activity/team (incl. team deep links). */
const PRIMARY_UNLINKED_ROUTES = [
    ROUTE_NAMES.WORK,
    ROUTE_NAMES.ACTIVITY,
    ROUTE_NAMES.TEAM,
    ROUTE_NAMES.TEAM_DASHBOARD,
    ROUTE_NAMES.TEAM_DOWNLINE_DASHBOARD,
    ROUTE_NAMES.TEAM_USER_WORK_MODE,
    ROUTE_NAMES.TEAM_DOWNLINE_WORK_MODE,
    ROUTE_NAMES.WORK_ENTITY,
    ROUTE_NAMES.DASHBOARD,
    ROUTE_NAMES.ALIGN,
    ROUTE_NAMES.DIRECTORY,
    ROUTE_NAMES.SETTINGS,
    ROUTE_NAMES.REPORTS,
    ROUTE_NAMES.DIVISION
];

const CHAT_PANEL_INIT_STATE = {
    visible: false,
    state: {
        data: null,
        openedTab: undefined,
        openedMembershipTab: undefined,
    },
    openedUnlinkedRoute: {
        name: undefined,
        params: null,
    },
    lastDetachedRoute: {
        name: undefined,
        params: null,
    },
    tracked: false,
};

const DIRECTORY_PANEL_INIT_STATE = {
    visible: false,
    openedTab: undefined,
    openedUnlinkedRoute: {
        name: undefined,
        params: null,
    },
    lastDetachedRoute: {
        name: undefined,
        params: null,
    },
    tracked: false,
};

const PRODUCTIVITY_PANEL_INIT_STATE = {
    visible: false,
    state: undefined,
    openedUnlinkedRoute: {
        name: undefined,
        params: null,
    },
    lastDetachedRoute: {
        name: undefined,
        params: null,
    },
    tracked: false,
};

export const useUnlinkedPanelState = defineStore("unlinkedPanelState", () => {
    const { chatPopover, toggleChatPopover } = utilities();
    const {
        is_productivity_opened,
        is_team_opened,
        is_new_directory_opened,
        isChatOpened,
    } = useWindowPopoverState();

    const isDirectoryOpened = computed(
        () => is_team_opened.value || is_new_directory_opened.value,
    );
    const appState = useAppState();
    const conversationStore = useConversation();
    const appChat = useChat();
    const route = useRoute();
    const router = useRouter();
    const rightDrawerStore = useRightDrawerStore();

    const saveTimeOut = ref(null);
    const restoreTimeOut = ref(null);
    const restrictRecovery = ref(false);

    const chatPanelState = ref({ ...CHAT_PANEL_INIT_STATE });

    const directoryPanelState = ref({ ...DIRECTORY_PANEL_INIT_STATE });

    const productivityPanelState = ref({ ...PRODUCTIVITY_PANEL_INIT_STATE });

    const previouslyChatPopoverOpened = ref({
        tracked: false,
        productivity: {
            opened: false,
        },
        directory: {
            opened: false,
        },
    });

    const isUnlinkedPanelActive = computed(() => {
        return PRIMARY_UNLINKED_ROUTES.includes(route?.name);
    });

    const isSpecialDelinkedPanelActive = computed(() => {
        const { ALIGN, DIVISION, DIRECTORY } = ROUTE_NAMES;
        const SPECIAL_ROUTES = [ALIGN, DIVISION, DIRECTORY];
        return SPECIAL_ROUTES.includes(route?.name);
    });

    const savedRoutes = computed(() => {
        return {
            productivity: productivityPanelState.value.openedUnlinkedRoute,
            directory: directoryPanelState.value.openedUnlinkedRoute,
            chat: chatPanelState.value.openedUnlinkedRoute,
        };
    });

    const lastDetachedRoute = computed(() => {
        return {
            productivity: productivityPanelState.value.lastDetachedRoute,
            directory: directoryPanelState.value.lastDetachedRoute,
            chat: chatPanelState.value.lastDetachedRoute,
        };
    });

    const routeDetails = computed(() => {
        return {
            name: route?.name,
            params: route?.params,
        };
    });

    function saveCurrentTrackOfWindowPopover(
        routeName,
        checkTracker = false,
        routeParams = null,
    ) {
        clearTimeout(saveTimeOut.value);
        const isDirectoryPanelOpened = isDirectoryOpened.value;
        const isProductivityOpened = is_productivity_opened.value;
        const isConvOpened = isChatOpened.value;

        saveTimeOut.value = setTimeout(() => {
            if (isProductivityOpened) {
                if (checkTracker && productivityPanelState.value.tracked)
                    return;
                turnOffAllVisible();
                saveProductivityPanelState(
                    routeName,
                    isProductivityOpened,
                    routeParams,
                );
                return;
            }

            if (isDirectoryPanelOpened) {
                if (checkTracker && directoryPanelState.value.tracked) return;
                turnOffAllVisible();
                saveDirectoryPanelState(
                    routeName,
                    isDirectoryPanelOpened,
                    routeParams,
                );
                return;
            }
            if (isConvOpened) {
                if (checkTracker && chatPanelState.value.tracked) return;
                turnOffAllVisible();
                saveChatPanelState(routeName, isConvOpened, routeParams);
                if (isUnlinkedPanelActive.value) {
                    console.log("saveCurrentTrackOfWindowPopover", route);
                    chatPanelState.value.lastDetachedRoute = {
                        name: routeName,
                        params: routeParams,
                    };
                }
                return;
            }
        }, 400);
    }

    function saveProductivityPanelState(
        oldRouteName,
        visible,
        routeParams = null,
    ) {
        const savedRoute = productivityPanelState.value?.openedUnlinkedRoute;
        const lastDetachedRoute =
            productivityPanelState.value?.lastDetachedRoute;
        productivityPanelState.value = {
            visible,
            state: appState.productive,
            openedUnlinkedRoute: savedRoute.name
                ? savedRoute
                : {
                      name: oldRouteName,
                      params: routeParams,
                  },
            tracked: visible ? true : productivityPanelState.value.tracked,
            lastDetachedRoute: lastDetachedRoute.name
                ? lastDetachedRoute
                : {
                      name: oldRouteName,
                      params: routeParams,
                  },
        };
    }

    function saveDirectoryPanelState(
        oldRouteName,
        visible,
        routeParams = null,
    ) {
        const savedRoute = directoryPanelState.value?.openedUnlinkedRoute;
        const lastDetachedRoute = directoryPanelState.value?.lastDetachedRoute;
        directoryPanelState.value = {
            visible,
            openedTab: appState.tab,
            openedUnlinkedRoute: savedRoute.name
                ? savedRoute
                : {
                      name: oldRouteName,
                      params: routeParams,
                  },
            tracked: visible ? true : directoryPanelState.value.tracked,
            lastDetachedRoute: lastDetachedRoute.name
                ? lastDetachedRoute
                : {
                      name: oldRouteName,
                      params: routeParams,
                  },
        };
    }

    function saveChatPanelState(oldRouteName, visible, routeParams = null) {
        const savedRoute = chatPanelState.value?.openedUnlinkedRoute;
        const lastDetachedRoute = chatPanelState.value?.lastDetachedRoute;
        chatPanelState.value = {
            visible,
            state: {
                data: conversationStore.active?.state?.originalData,
                openedTab: appChat.tab,
                openedMembershipTab: appState.selected_variant_tab,
            },
            openedUnlinkedRoute: savedRoute.name
                ? savedRoute
                : {
                      name: oldRouteName,
                      params: routeParams,
                  },
            lastDetachedRoute,
            tracked: true,
        };
    }

    function savePreviousStateOfChatPopover(opened) {
        previouslyChatPopoverOpened.value.tracked = true;
        if (is_productivity_opened.value) {
            previouslyChatPopoverOpened.value.productivity.opened = opened;
            return;
        }

        if (isDirectoryOpened.value) {
            previouslyChatPopoverOpened.value.directory.opened = opened;
            return;
        }
    }

    function restoreWindowPopoverState() {
        clearTimeout(restoreTimeOut.value);
        restoreTimeOut.value = setTimeout(() => {
            if (productivityPanelState.value.visible) {
                restoreProductivityPanelState();
                toggleChatPopover(true);
            }

            if (directoryPanelState.value.visible) {
                restoreDirectoryPanelState();
                toggleChatPopover(true);
            }

            if (chatPanelState.value.visible) {
                restoreChatPanelState();
                toggleChatPopover(true);
            }
        }, 300);
    }

    function restoreProductivityPanelState() {
        appState.toggleProductivityDrawer(true, false);
        appState.updateProductiveState({
            ...productivityPanelState.value.state,
            productivity_opened: true,
        });
        productivityPanelState.value = { ...PRODUCTIVITY_PANEL_INIT_STATE };
    }

    function restoreDirectoryPanelState() {
        appState.toggleNewDirectoryPanel(true);
        directoryPanelState.value = { ...DIRECTORY_PANEL_INIT_STATE };
    }

    async function restoreChatPanelState() {
        appChat.setTab(chatPanelState.value.state.openedTab);
        appState.setSelectedVariantTab(
            chatPanelState.value.state.openedMembershipTab,
        );
        chatPanelState.value = { ...CHAT_PANEL_INIT_STATE };
    }

    function turnOffAllVisible() {
        chatPanelState.value.visible = false;
        directoryPanelState.value.visible = false;
        productivityPanelState.value.visible = false;
    }

    function restrictRecoveryMode() {
        if (isUnlinkedPanelActive.value) return;
        restrictRecovery.value = true;
    }

    function unrestrictRecoveryMode() {
        restrictRecovery.value = false;
    }

    function openSavedProductivityRoute() {
        const route = savedRoutes.value.productivity;
        if (route.name) {
            productivityPanelState.value.openedUnlinkedRoute = {
                ...PRODUCTIVITY_PANEL_INIT_STATE.openedUnlinkedRoute,
            };
            router.push({
                name: route.name,
                params: route.params ?? {},
            });
        }
    }

    function openSavedDirectoryRoute() {
        const route = savedRoutes.value.directory;
        if (route.name) {
            directoryPanelState.value.openedUnlinkedRoute = {
                ...DIRECTORY_PANEL_INIT_STATE.openedUnlinkedRoute,
            };
            router.push({
                name: route.name,
                params: route.params ?? {},
            });
        }
    }

    function openSavedChatRoute() {
        const route = savedRoutes.value.chat;
        if (route.name) {
            chatPanelState.value.openedUnlinkedRoute = {
                ...CHAT_PANEL_INIT_STATE.openedUnlinkedRoute,
            };
            router.push({
                name: route.name,
                params: route.params ?? {},
            });
        }
    }

    function resetPreviouslyChatPopoverOpened() {
        previouslyChatPopoverOpened.value.tracked = false;
        previouslyChatPopoverOpened.value.productivity.opened = false;
        previouslyChatPopoverOpened.value.directory.opened = false;
    }

    function openProductivityPanelTracked(open = true) {
        if (open && !isSpecialDelinkedPanelActive.value) {
            switchToLastDetachProductivityRouteFromUnlinkedPanel();
        }
    }

    function openDirectoryPanelTracked(open = true) {
        const alreadyPanelOpened = chatPopover.value;
        const willOpen =
            open || previouslyChatPopoverOpened.value.directory.opened;
        toggleChatPopover(willOpen, true, true);
        resetPreviouslyChatPopoverOpened();
        savePreviousStateOfChatPopover(alreadyPanelOpened);
        if (open && !isSpecialDelinkedPanelActive.value) {
            switchToLastDetachDirectoryRouteFromUnlinkedPanel();
        }
    }

    function switchToLastSavedRoute() {
        if (savedRoutes.value.productivity?.name) {
            openSavedProductivityRoute();
            return;
        }

        if (savedRoutes.value.directory?.name) {
            openSavedDirectoryRoute();
            return;
        }

        if (savedRoutes.value.chat?.name) {
            openSavedChatRoute();
            return;
        }

        appState.setWorkAsSelectedModule();
        appState.goToWork();
        return;
    }

    function switchToLastDetachProductivityRouteFromUnlinkedPanel(
        forceRedirect = false,
    ) {
        if (!forceRedirect && isUnlinkedPanelActive.value) return;
        if (isUnlinkedPanelActive.value) return;
        const detahcedRoute = lastDetachedRoute.value.productivity;
        if (detahcedRoute.name) {
            router.push({
                name: detahcedRoute.name,
                params: detahcedRoute.params ?? {},
            });
        } else {
            switchToLastSavedRoute();
        }
    }

    function switchToLastDetachDirectoryRouteFromUnlinkedPanel(
        forceRedirect = false,
    ) {
        if (!forceRedirect && isUnlinkedPanelActive.value) return;
        const detahcedRoute = lastDetachedRoute.value.directory;
        if (detahcedRoute.name) {
            router.push({
                name: detahcedRoute.name,
                params: detahcedRoute.params ?? {},
            });
        } else {
            switchToLastSavedRoute();
        }
    }

    function switchToLastDetachChatRouteFromUnlinkedPanel(
        forceRedirect = false,
    ) {
        if (
            !forceRedirect &&
            (isUnlinkedPanelActive.value || isSpecialDelinkedPanelActive.value)
        )
            return;
        const detahcedRoute = lastDetachedRoute.value.chat;
        console.log("detahcedRoute", detahcedRoute.params);
        console.log("useConversation().active", useConversation().active);
        if (detahcedRoute.name) {
            if (!useConversation().active?.is_item) {
                useAppState().skip_locate_animation = true;
            }

            router.push({
                name: detahcedRoute.name,
                params: detahcedRoute.params ?? {},
            });
        } else {
            switchToLastSavedRoute();
        }
    }

    function checkIfUnlinkedPanelActive(routeName) {
        return PRIMARY_UNLINKED_ROUTES.includes(routeName);
    }

    watch(isDirectoryOpened, (newValue, oldValue) => {
        if (
            oldValue === true &&
            newValue === false &&
            isUnlinkedPanelActive.value
        ) {
            saveDirectoryPanelState(route.name, false, route.params ?? {});
            directoryPanelState.value.lastDetachedRoute = {
                name: route.name,
                params: route.params ?? {},
            };
        }
    });

    watch(is_productivity_opened, (newValue, oldValue) => {
        if (
            oldValue === true &&
            newValue === false &&
            isUnlinkedPanelActive.value
        ) {
            saveProductivityPanelState(route.name, false, route.params ?? {});
            productivityPanelState.value.lastDetachedRoute = {
                name: route.name,
                params: route.params ?? {},
            };
        }
    });

    watch(isChatOpened, (newValue) => {
        if (isUnlinkedPanelActive.value) {
            saveChatPanelState(route.name, newValue);
            chatPanelState.value.lastDetachedRoute = {
                name: route.name,
                params: route.params ?? {},
            };
        }
    });

    watch(
        chatPopover,
        (newValue, oldValue) => {
            if (newValue) {
                savePreviousStateOfChatPopover(oldValue);
            }
        },
        { immediate: true },
    );

    watch(
        routeDetails,
        (newRouteDetails, oldRouteDetails) => {
            const ROUTES = PRIMARY_UNLINKED_ROUTES;

            const newRouteName = newRouteDetails?.name;
            const newRouteParams = newRouteDetails?.params;
            const oldRouteName = oldRouteDetails?.name;
            const oldRouteParams = oldRouteDetails?.params;

            if (
                (ROUTES.includes(newRouteName) &&
                    ROUTES.includes(oldRouteName)) ||
                (isSpecialDelinkedPanelActive.value && isChatOpened.value)
            ) {
                saveCurrentTrackOfWindowPopover(
                    newRouteName,
                    false,
                    newRouteParams,
                );
                return;
            }

            if (
                !ROUTES.includes(oldRouteName) &&
                !ROUTES.includes(newRouteName)
            ) {
                return;
            }
            if (ROUTES.includes(newRouteName)) {
                rightDrawerStore.closeDrawer();
                if (restrictRecovery.value) return;
                restoreWindowPopoverState();
            } else {
                saveCurrentTrackOfWindowPopover(
                    oldRouteName,
                    true,
                    oldRouteParams,
                );
                toggleChatPopover(false);
            }
        },
        { immediate: true },
    );

    return {
        isSpecialDelinkedPanelActive,
        chatPanelState,
        directoryPanelState,
        productivityPanelState,
        restrictRecoveryMode,
        unrestrictRecoveryMode,
        isUnlinkedPanelActive,
        savedRoutes,
        openSavedProductivityRoute,
        openSavedDirectoryRoute,
        openSavedChatRoute,
        previouslyChatPopoverOpened,
        openProductivityPanelTracked,
        openDirectoryPanelTracked,
        switchToLastSavedRoute,
        lastDetachedRoute,
        switchToLastDetachProductivityRouteFromUnlinkedPanel,
        switchToLastDetachDirectoryRouteFromUnlinkedPanel,
        switchToLastDetachChatRouteFromUnlinkedPanel,
        checkIfUnlinkedPanelActive,
    };
});
